import { useEffect, useState } from "react";
import { financeiroService } from "./service/financeiro.service";
import {
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Modal,
  TableCell,
  Typography,
} from "@mui/material";
import { ConstructionMovementEnum } from "../../shared/utils/enum/enum";
import { formatDate, formatDateBR } from "../../shared/utils";
import { modalidade } from "./schema/gerar-movimentacao-obra.schema";
import { ENVIRONMENT } from "../../environment/environment";

interface VisualizarMovementModalProps {
  open: boolean;
  onClose: () => void;
  idMovimentacao: string;
}

export function ModalDetalhamentoMovimentacaoFinanceira({
  open,
  onClose,
  idMovimentacao,
}: VisualizarMovementModalProps) {
  const [detalheMovimentacao, setDetalheMovimentacao] = useState<any>(null);
  const baseUrl = ENVIRONMENT.BASE_URL_API;

  useEffect(() => {
    const fetchDetalheMovimentacao = () => {
      if (idMovimentacao) {
        financeiroService
          .detalhar(idMovimentacao)
          .then((data) => {
            console.log("Detalhes da movimentação:", data);
            setDetalheMovimentacao(data);
          })
          .catch((error) => {
            console.error("Erro ao buscar detalhes da movimentação:", error);
          });
      }
    };
    fetchDetalheMovimentacao();
  }, [idMovimentacao]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        },
      }}
    >
      <Box
        p={4}
        borderRadius={2}
        boxShadow={24}
        minWidth={350}
        maxWidth={400}
        overflow={"auto"}
        mx="auto"
        mt="10vh"
        display="flex"
        flexDirection="column"
        gap={2}
        bgcolor="background.paper" // Use a cor do tema
        color="text.primary"
      >
        <Typography variant="h6">Movimentação detalhada</Typography>
        {detalheMovimentacao && (
          <List sx={{ height: "480px", overflowY: "auto" }}>
            {detalheMovimentacao?.client && (
              <>
                <ListItem>
                  <ListItemText
                    primary="Cliente"
                    secondary={
                      detalheMovimentacao?.client?.fantasyName ||
                      detalheMovimentacao?.client?.corporateName ||
                      detalheMovimentacao?.client?.fullName ||
                      "-"
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Obra"
                    secondary={detalheMovimentacao?.work?.workName || "-"}
                  />
                </ListItem>
              </>
            )}

            <TableCell align="center" sx={{ minWidth: 100, textAlign: "left" }}>
              {detalheMovimentacao.status ===
                ConstructionMovementEnum.GENERATED ? (
                <Chip
                  label="Gerado"
                  color="success"
                  size="small"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label="Cancelado"
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
            </TableCell>

            {detalheMovimentacao?.employee && (
              <ListItem>
                <ListItemText
                  primary="Colaborador"
                  secondary={
                    detalheMovimentacao?.employee?.fullName ||
                    detalheMovimentacao?.employee?.corporateName ||
                    "-"
                  }
                />
              </ListItem>
            )}

            <ListItem>
              <ListItemText
                primary="Data da Movimentação"
                secondary={
                  formatDateBR(formatDate(detalheMovimentacao?.dateMovement)) ||
                  "-"
                }
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Setor"
                secondary={detalheMovimentacao?.setor?.name || "-"}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Plano de Contas"
                secondary={
                  detalheMovimentacao?.chartOfAccounts?.code +
                  " -> " +
                  detalheMovimentacao?.chartOfAccounts?.description || "-"
                }
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Responsável por gerar a movimentação"
                secondary={detalheMovimentacao?.user?.fullName || "-"}
              />
            </ListItem>
            {detalheMovimentacao?.status ===
              ConstructionMovementEnum.CANCELED && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="Data de Cancelamento"
                      secondary={
                        formatDateBR(
                          formatDate(detalheMovimentacao?.cancellationDate)
                        ) || "-"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Responsável por cancelar a movimentação"
                      secondary={
                        detalheMovimentacao?.canceledUser?.fullName || "-"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Motivo do Cancelamento"
                      secondary={detalheMovimentacao?.cancellationReason || "-"}
                    />
                  </ListItem>
                </>
              )}
            <ListItem>
              <ListItemText
                primary="Valor Adicional"
                secondary={
                  detalheMovimentacao?.value.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                    minimumFractionDigits: 2,
                  }) || "-"
                }
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Quantidade a Pagar"
                secondary={detalheMovimentacao?.description || "-"}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Quantidade a Pagar"
                secondary={
                  detalheMovimentacao?.quantityToPay +
                  " - " +
                  modalidade[detalheMovimentacao?.unit] || "-"
                }
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Visualização Comprovante de Pagamento"
                secondary={
                  detalheMovimentacao?.paymentProof ? (
                    <a
                      href={`${baseUrl}/financi-moviment/download-proof/${idMovimentacao}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Baixar Comprovante
                    </a>
                  ) : (
                    "-"
                  )
                }
              />
            </ListItem>
            {detalheMovimentacao?.bank && (
              <ListItem>
                <ListItemText
                  primary="Dados bancários para movimentação"
                  secondary={
                    "Banco: " + detalheMovimentacao?.bank?.code + " - " + detalheMovimentacao?.bank?.name +
                    " | Agência: " + detalheMovimentacao?.agency.agencyNumber+ " - " + detalheMovimentacao?.agency.digit +
                    " | Conta: " + detalheMovimentacao?.account.accountNumber + " - " + detalheMovimentacao?.account.digit ||
                    "-"
                  }
                />
              </ListItem>
            )}
            {detalheMovimentacao?.receiptPdf && (
              <ListItem>
                <Button
                  variant="outlined"
                  color="primary"
                  href={`${baseUrl}/${detalheMovimentacao.receiptPdf}`}
                  target="_blank"
                >
                  Visualizar Comprovante de Pagamento
                </Button>
              </ListItem>
            )}
          </List>
        )}
        <Button variant="outlined" onClick={() => onClose()} color="inherit">
          Fechar
        </Button>
      </Box>
    </Modal>
  );
}
